"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { FormInput } from "@/components/form/form-input";
import { PasswordRequirements } from "@/features/auth/components/elements/password-requirements";

/**
 * Password input paired with the live requirement checklist. Shared by the
 * registration and password-reset steps, which differ only in the field label.
 */
export const NewPasswordField = <T extends FieldValues>({
  form,
  fieldName,
  label,
  alwaysShowRequirements = false,
}: {
  form: UseFormReturn<T, unknown, T>;
  fieldName: Path<T>;
  label: string;
  alwaysShowRequirements?: boolean;
}) => {
  const t = useTranslations("auth.password");
  const [isVisible, setIsVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const value = useWatch({ control: form.control, name: fieldName });
  const visibilityLabel = isVisible ? t("hide") : t("show");

  return (
    // Focus is tracked on the wrapper because React's focus events bubble —
    // the checklist has to appear as soon as the field is in play.
    <div
      className="flex flex-col gap-2"
      onBlur={() => setIsFocused(false)}
      onFocus={() => setIsFocused(true)}
    >
      <FormInput
        autoComplete="new-password"
        fieldLabel={label}
        fieldName={fieldName}
        form={form}
        onTrailingIconClick={() => setIsVisible((current) => !current)}
        required
        trailingIcon={
          <span className="font-medium text-sm text-text leading-[22px]">
            {visibilityLabel}
          </span>
        }
        trailingIconLabel={visibilityLabel}
        type={isVisible ? "text" : "password"}
        validateEarly
      />
      <PasswordRequirements
        alwaysVisible={alwaysShowRequirements}
        isFocused={isFocused}
        value={value ?? ""}
      />
    </div>
  );
};
